"use client";

import Image from "next/image";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BlockProps } from "../BlocksManager/registry";

function renderMarkedTitle(title: string) {
  const parts = title.split(/(\[.*?\])/g);

  return parts.map((part, index) => {
    const isHighlighted = part.startsWith("[") && part.endsWith("]");

    if (isHighlighted) {
      const text = part.slice(1, -1);

      return (
        <span
          key={index}
          className="text-blue font-lobster"
        >
          
          {text}
        </span>
      );
    }

    return <span key={index}>{part}</span>;
  });
}

function renderDescription(description: string) {
  const parts = description.split(/(\*\*.*?\*\*)/g);

  return parts.map((part, index) => {
    const isHighlighted = part.startsWith("**") && part.endsWith("**");

    if (isHighlighted) {
      const text = part.slice(2, -2);

      return (
        <span key={index} className="font-bold">
          {text}
        </span>
      );
    }

    return <span key={index}>{part}</span>;
  });
}



export default function SelectionSection({ data }: BlockProps) {
  if (!data) return null;

  const items = data.plats || [];
  const [currentIndex, setCurrentIndex] = useState(0);

  const goLeft = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const goRight = () => {
    setCurrentIndex((prev) => Math.min(items.length - 1, prev + 1));
  };

  return (
    <section className="bg-violet-light py-10" id="nos-plats">
      <div className="container mx-auto px-6">
        <div className="mx-auto max-w-[980px] text-center">
          <h2 className="font-literata text-[38px] font-bold leading-[1.25] text-black md:text-[60px]">
            {renderMarkedTitle(data.titre || "")}
          </h2>

          <div className="mx-auto mt-4 max-w-[980px]">
            <p className="text-xl lg:text-[26px] ">
              {renderDescription(data.description || "")}
            </p>
          </div>
        </div>

        {/* Mobile — carousel */}
        <div className="relative mt-8 md:hidden">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {items.map((item: any) => (
                <div key={item.id} className="w-full shrink-0 px-4">
                  <article className="mx-auto flex w-full max-w-[340px] flex-col items-center rounded-[100px] bg-white px-8 pb-10 pt-10 text-center">
                    <div className="relative h-[280px] w-[280px]">
                      {item.image?.url && (
                        <Image
                          src={item.image.url}
                          alt={item.image.alternativeText || item.nom}
                          fill
                          className="object-contain"
                        />
                      )}
                    </div>

                    <h4 className="font-heading mt-8 max-w-[320px] text-[34px] font-medium leading-[1.02] text-black">
                      {item.nom}
                    </h4>
                  </article>
                </div>
              ))}
            </div>
          </div>

          {items.length > 1 && (
            <div className="mt-4 flex items-center justify-center gap-6">
              <button
                type="button"
                onClick={goLeft}
                disabled={currentIndex === 0}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-md disabled:opacity-30"
              >
                <ChevronLeft className="h-5 w-5 text-black" />
              </button>

              <div className="flex gap-2">
                {items.map((_: any, i: number) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setCurrentIndex(i)}
                    className={`h-2 w-2 rounded-full transition-all ${
                      i === currentIndex ? "w-5 bg-blue" : "bg-blue/30"
                    }`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={goRight}
                disabled={currentIndex === items.length - 1}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-md disabled:opacity-30"
              >
                <ChevronRight className="h-5 w-5 text-black" />
              </button>
            </div>
          )}
        </div>

        {/* Desktop — grille */}
        <div className="mt-14 hidden md:grid md:grid-cols-2 md:gap-8">
          {items.map((item: any) => (
            <article
              key={item.id}
              className="flex min-h-[520px] flex-col items-center rounded-[100px] bg-white px-8 pb-10 pt-10 text-center"
            >
              <div className="relative h-[280px] w-[280px] md:h-[340px] md:w-[340px]">
                {item.image?.url && (
                  <Image
                    src={item.image.url}
                    alt={item.image.alternativeText || item.nom}
                    fill
                    className="object-contain"
                  />
                )}
              </div>

              <h3 className="font-heading mt-8 max-w-[320px] text-[38px] font-medium leading-[1.02] text-black md:text-[45px]">
                {item.nom}
              </h3>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}